import { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { Line } from "react-chartjs-2";
import { gChatlogItem } from "../../interfaces";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler } from "chart.js";
import debounce from "lodash/debounce";
import style from "./ChartAnalyticsBar.module.css";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler);

export interface ChatUserFilterState {
    visibleUsers: Set<string>;
    isMultiUser: boolean;
}

type ChartAnalyticsBarProps = {
    data: gChatlogItem[];
    onFilterChange?: (filterState: ChatUserFilterState) => void;
};

// Color palette for multi-user view
const USER_COLORS = [
    { bg: "rgba(0, 198, 175, 0.15)", border: "rgba(0, 198, 175, 1)" },
    { bg: "rgba(0, 120, 212, 0.15)", border: "rgba(0, 120, 212, 1)" },
    { bg: "rgba(156, 39, 176, 0.15)", border: "rgba(156, 39, 176, 1)" },
    { bg: "rgba(255, 152, 0, 0.15)", border: "rgba(255, 152, 0, 1)" },
    { bg: "rgba(76, 175, 80, 0.15)", border: "rgba(76, 175, 80, 1)" },
    { bg: "rgba(233, 30, 99, 0.15)", border: "rgba(233, 30, 99, 1)" },
    { bg: "rgba(63, 81, 181, 0.15)", border: "rgba(63, 81, 181, 1)" },
    { bg: "rgba(121, 85, 72, 0.15)", border: "rgba(121, 85, 72, 1)" },
];

const ChartAnalyticsBar = ({ data, onFilterChange }: ChartAnalyticsBarProps) => {
    const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: 400 });
    const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());
    const chartRef = useRef<any>(null);

    useEffect(() => {
        const handleResize = debounce(() => {
            const newWidth = window.innerWidth;
            setDimensions(prev => ({ ...prev, width: newWidth }));

            // Force chart to resize
            if (chartRef.current) {
                chartRef.current.resize();
            }
        }, 100);

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            if (handleResize?.cancel) handleResize.cancel();
        };
    }, []);

    const { chartData, maxValue, isMultiUser, uniqueUsers } = useMemo(() => {
        // Detect unique users
        const uniqueUsers = [...new Set(data.map(item => item.usermail).filter(Boolean))] as string[];
        const isMultiUser = uniqueUsers.length > 1;

        // Aggregate data by date and user
        const userDateCounts: { [usermail: string]: { [date: string]: number } } = {};
        const allDates = new Set<string>();

        data.forEach(item => {
            const usermail = item.usermail || 'Unknown';
            const date = item.key; // date is already formatted as key
            allDates.add(date);

            if (!userDateCounts[usermail]) userDateCounts[usermail] = {};
            userDateCounts[usermail][date] = (userDateCounts[usermail][date] || 0) + 1;
        });

        const sortedDates = [...allDates].sort();

        // Create datasets for each user with consistent colors
        const datasets = uniqueUsers.map((usermail, index) => {
            const colorSet = USER_COLORS[index % USER_COLORS.length];
            const countsData = sortedDates.map(date => userDateCounts[usermail]?.[date] || 0);

            return {
                label: usermail,
                data: countsData,
                backgroundColor: colorSet.bg,
                borderColor: colorSet.border,
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointHoverRadius: 5,
                pointBackgroundColor: colorSet.border,
                pointBorderColor: "#fff",
                pointBorderWidth: 1.5,
                pointHoverBackgroundColor: "#fff",
                pointHoverBorderColor: colorSet.border,
                pointHoverBorderWidth: 2,
                yAxisID: 'y'
            };
        });

        // Calculate max value for scale
        let maxVal = 0;
        sortedDates.forEach(date => {
            let dateTotal = 0;
            uniqueUsers.forEach(user => { dateTotal += userDateCounts[user]?.[date] || 0 });
            maxVal = Math.max(maxVal, dateTotal);
        });

        return {
            chartData: { labels: sortedDates, datasets },
            maxValue: Math.ceil(maxVal * 1.1),
            isMultiUser,
            uniqueUsers
        };
    }, [data]);

    // Notify parent when filter changes (multi-user mode only)
    useEffect(() => {
        if (onFilterChange && isMultiUser && uniqueUsers) {
            const visibleUsers = new Set<string>(uniqueUsers.filter((_: string, index: number) => !hiddenDatasets.has(index)));
            onFilterChange({ visibleUsers, isMultiUser: true });
        }
    }, [hiddenDatasets, isMultiUser, uniqueUsers, onFilterChange]);

    // Reset hidden datasets when data changes significantly
    useEffect(() => {
        setHiddenDatasets(new Set());
    }, [uniqueUsers?.length]);

    // Custom legend click handler
    const handleLegendClick = useCallback((e: any, legendItem: any, legend: any) => {
        const index = legendItem.datasetIndex;
        const chart = legend.chart;

        // Toggle dataset visibility (default Chart.js behavior)
        if (chart.isDatasetVisible(index)) {
            chart.hide(index);
        } else {
            chart.show(index);
        }

        // Update our tracking state
        setHiddenDatasets(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    }, []);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false,
                },
                ticks: {
                    font: { size: 11, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                    color: '#605e5c',
                }
            },
            y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                min: 0,
                max: maxValue,
                stacked: isMultiUser,
                grid: { display: true, color: 'rgba(0, 0, 0, 0.05)', drawBorder: false },
                ticks: {
                    font: { size: 11, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                    color: '#605e5c',
                    callback: function(value: any) {
                        return Math.round(value);
                    }
                },
                title: {
                    display: true,
                    text: 'Interactions',
                    color: 'rgba(0, 198, 175, 1)',
                    font: { size: 12, weight: 600, family: "'Urbanist', system-ui, -apple-system, sans-serif" }
                }
            }
        },
        plugins: {
            legend: {
                position: 'top' as const,
                align: 'end' as const,
                onClick: isMultiUser ? handleLegendClick : undefined,
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 15,
                    font: { size: 12, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif", weight: 500 },
                    color: '#323130',
                }
            },
            title: {
                display: true,
                text: isMultiUser ? 'User Interactions Over Time' : 'Interactions Over Time',
                color: '#323130',
                font: { size: 16, weight: 600, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                padding: { top: 10, bottom: 20 }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#323130',
                bodyColor: '#605e5c',
                borderColor: '#e1e4e8',
                borderWidth: 1,
                padding: 12,
                boxPadding: 6,
                usePointStyle: true,
                titleFont: { size: 13, weight: 600, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                bodyFont: { size: 12, family: "'Raleway', 'Urbanist', system-ui, -apple-system, sans-serif" },
                callbacks: {
                    label: function(context: any) {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';
                        label += context.parsed.y;
                        return label;
                    }
                }
            }
        },
    }), [maxValue, isMultiUser, handleLegendClick]);

    return (
        <div  className={style.chartMainWrap}>
            <Line ref={chartRef} data={chartData} options={options} />
        </div>
    );
};
export default ChartAnalyticsBar;
